import React, { FC, useState, useCallback, useMemo } from "react";
import { Modal } from "antd";
import { apiService } from "@/services";
import { debounce } from "lodash";
import { Search, X } from "lucide-react";
import SearchResultItem from "./search-result";
import { useRouter } from "next/navigation";

interface CustomModalProps {
	open: boolean;
	onClose: () => void;
}

const SearchModal: FC<CustomModalProps> = ({ open, onClose }) => {
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [searchResults, setSearchResults] = useState<any[]>([]);
	const router = useRouter();

	const handleSearch = useCallback(async (query: string) => {
		if (query.length < 3) return;
		try {
			const searchResults = await apiService.get(`search?search=${query}`);
			setSearchResults(searchResults.data);
		} catch (error) {
			console.error("Search error:", error);
		}
	}, []);

	const debouncedHandleSearch = useMemo(
		() => debounce(handleSearch, 500),
		[handleSearch]
	);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchTerm(value);

		if (value.length < 1) {
			setSearchResults([]); // Clear results when input is empty
		} else {
			debouncedHandleSearch(value); // Trigger search for non-empty input
		}
	};

	const handleClear = () => {
		setSearchTerm("");
		setSearchResults([]);
	};

	const handleClose = () => {
		setSearchTerm("");
		setSearchResults([]);
		onClose();
	};

	const handleNFTSelect = (nftId: string) => {
		router.push(`/?n=${nftId}`);
		handleClose();
	};

	return (
		<Modal
			open={open}
			onCancel={handleClose}
			className="custom-modal"
			footer={null}
			zIndex={9998}
		>
			<div className="modal-body pt-5 pb-3">
				<div className="relative">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400 pointer-events-none" />
					<input
						type="text"
						className="w-full pl-10 pr-10 py-3 text-white bg-stone-900 rounded-md"
						value={searchTerm}
						onChange={handleInputChange}
						placeholder="Search by name, location, or traits!"
						aria-label="Search input"
						autoFocus
					/>
					{searchTerm.length > 0 && (
						<button
							type="button"
							onClick={handleClear}
							aria-label="Clear search"
							className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
						>
							<X className="size-4" />
						</button>
					)}
				</div>
			</div>

			{searchResults.length > 0 ? (
				<div className="search-results max-h-[60vh] overflow-y-auto pr-1 -mr-1">
					{searchResults.map((pin) => (
						<button
							type="button"
							key={pin._id}
							onClick={() => handleNFTSelect(pin._id)}
							className="cursor-pointer w-full text-left"
						>
							<SearchResultItem pin={pin} />
						</button>
					))}
				</div>
			) : (
				<div className="no-results text-gray-500 pb-2">
					{searchTerm.length === 0
						? "Start typing to search NFTs by name, location, or traits."
						: searchTerm.length < 3
							? "Keep typing - search starts after 3 characters."
							: `No matches for "${searchTerm}". Try a different name or trait.`}
				</div>
			)}
		</Modal>
	);
};

export default SearchModal;
